import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../design-system/atoms/Button';
import { Card } from '../design-system/atoms/Card';
import { Spinner } from '../design-system/atoms/Spinner';
import { C, F } from '../design-system/tokens';
import { completePasswordRecovery, logoutUser } from '../domains/auth/api';
import { isSupabaseConfigured, requireSupabase } from '../domains/auth/supabaseClient';

// 1.30.0 / P0-4 — Завершение сброса пароля.
//
// На эту страницу попадают через ссылку в письме от Supabase. Supabase JS
// SDK на этом этапе уже распарсил hash-параметры (`#access_token=...&
// type=recovery&...`) и открыл временную recovery-сессию. В authStore
// событие PASSWORD_RECOVERY перехватывается eager-listener'ом и НЕ
// устанавливает user в стор, чтобы не сработал RoleRedirect.
//
// Здесь мы:
//   1) убеждаемся, что recovery-сессия активна (через getSession);
//   2) показываем форму нового пароля;
//   3) при submit вызываем completePasswordRecovery → выходим из
//      recovery-сессии → редирект на /login.
//
// Дополнительная подписка на onAuthStateChange в этом компоненте —
// страховка: даже если по какой-то причине eager-listener в authStore
// не успел подписаться до того, как SDK эмитнул PASSWORD_RECOVERY (что
// крайне маловероятно, но JS-микротаски — мутная вещь), здесь мы поймаем
// событие повторно. onAuthStateChange всегда переигрывает текущее
// состояние подписчику при subscribe — это часть контракта Supabase.
type Stage = 'checking' | 'ready' | 'no_session' | 'done';

export function ResetPassword() {
  const navigate = useNavigate();
  const [stage, setStage] = useState<Stage>(isSupabaseConfigured ? 'checking' : 'no_session');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isSupabaseConfigured) return;

    let mounted = true;

    // Сразу проверяем — есть ли активная сессия. Если юзер пришёл по
    // действительной ссылке, она уже должна быть к моменту маунта,
    // потому что SDK парсит hash при создании клиента.
    const probe = async () => {
      try {
        const { data } = await requireSupabase().auth.getSession();
        if (!mounted) return;
        if (data.session) {
          setStage('ready');
        } else {
          // Дадим SDK ещё один тик: hash-parsing идёт через микротаск, и
          // в edge-кейсах getSession может вернуть null на первом тике
          // (особенно в Safari). Подписка ниже добьёт случай, если
          // сессия появится чуть позже.
          setStage('no_session');
        }
      } catch {
        if (mounted) setStage('no_session');
      }
    };
    void probe();

    const { data: sub } = requireSupabase().auth.onAuthStateChange((event, session) => {
      if (!mounted) return;
      if (event === 'PASSWORD_RECOVERY' || (event === 'SIGNED_IN' && session)) {
        setStage('ready');
      }
    });

    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);

    if (password.length < 8) {
      setError('Пароль должен быть не короче 8 символов');
      return;
    }
    if (password !== confirm) {
      setError('Пароли не совпадают');
      return;
    }

    setLoading(true);
    try {
      await completePasswordRecovery(password);
      // Выходим из recovery-сессии, чтобы пользователь зашёл уже как
      // обычный — с новым паролем. Это также гарантирует, что в URL
      // не останется access_token из recovery (Supabase сам очищает
      // hash после updateUser, но signOut для полноты).
      try {
        await logoutUser();
      } catch {
        // не блокируем успех на ошибке signOut — пароль уже сменён.
      }
      setStage('done');
      // Через секунду уведём на /login. Даём короткий момент, чтобы
      // юзер увидел success-стейт.
      window.setTimeout(() => {
        navigate('/login', { replace: true });
      }, 1500);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось сменить пароль');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div
      style={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 18,
        background: C.bg,
      }}
    >
      <Card
        style={{
          width: '100%',
          maxWidth: 400,
          padding: '24px 20px',
          display: 'flex',
          flexDirection: 'column',
          gap: 20,
        }}
      >
        <Brand
          subtitle={
            stage === 'done'
              ? 'Пароль обновлён'
              : stage === 'no_session'
              ? 'Ссылка недействительна'
              : 'Новый пароль'
          }
        />

        {stage === 'checking' ? (
          <Spinner label="Проверяем ссылку..." />
        ) : stage === 'no_session' ? (
          <NoSessionNotice />
        ) : stage === 'done' ? (
          <DoneNotice />
        ) : (
          <form onSubmit={handleSubmit} style={{ display: 'grid', gap: 12 }}>
            <p style={{ color: C.sub, fontSize: 13, lineHeight: 1.5, margin: 0 }}>
              Придумайте новый пароль для входа. Минимум 8 символов.
            </p>

            <label style={{ display: 'grid', gap: 6 }}>
              <span style={fieldLabelStyle}>Новый пароль</span>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                autoComplete="new-password"
                minLength={8}
                required
                style={inputStyle}
              />
            </label>

            <label style={{ display: 'grid', gap: 6 }}>
              <span style={fieldLabelStyle}>Повторите пароль</span>
              <input
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                placeholder="••••••••"
                autoComplete="new-password"
                minLength={8}
                required
                style={inputStyle}
              />
            </label>

            {error ? <ErrorBox text={error} /> : null}

            <Button
              type="submit"
              fw
              sz="lg"
              loading={loading}
              ch="ОБНОВИТЬ ПАРОЛЬ"
            />
          </form>
        )}

        {stage !== 'done' ? (
          <div style={{ textAlign: 'center', marginTop: 4 }}>
            <Link
              to="/login"
              style={{
                color: C.sub,
                fontSize: 12,
                textDecoration: 'none',
                fontFamily: F.head,
                fontWeight: 600,
                letterSpacing: '.4px',
              }}
            >
              ← Назад ко входу
            </Link>
          </div>
        ) : null}
      </Card>
    </div>
  );
}

function Brand({ subtitle }: { subtitle: string }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, textAlign: 'center' }}>
      <div
        style={{
          width: 64,
          height: 64,
          borderRadius: 18,
          background: `linear-gradient(145deg, ${C.acc}cc, ${C.acc})`,
          boxShadow: `0 8px 28px ${C.acc}55`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <svg width="34" height="34" viewBox="0 0 34 34" fill="none">
          <path
            d="M17 3L30 10.5V23.5L17 31L4 23.5V10.5L17 3Z"
            stroke="rgba(255,255,255,0.9)"
            strokeWidth="2"
            fill="none"
          />
          <path
            d="M12 12h10M12 17h7M12 22h4"
            stroke="white"
            strokeWidth="2.2"
            strokeLinecap="round"
          />
        </svg>
      </div>
      <div>
        <div
          style={{
            fontFamily: F.head,
            fontWeight: 800,
            fontSize: 26,
            color: C.txt,
            letterSpacing: '-.3px',
          }}
        >
          Кворум
        </div>
        <div style={{ color: C.sub, marginTop: 4, fontSize: 13, lineHeight: 1.5 }}>
          {subtitle}
        </div>
      </div>
    </div>
  );
}

function NoSessionNotice() {
  return (
    <div
      style={{
        padding: '14px',
        borderRadius: 10,
        background: C.amS,
        border: `1px solid ${C.am}33`,
        color: C.txt,
        fontSize: 13,
        lineHeight: 1.55,
      }}
    >
      Эта ссылка устарела или открыта на другом устройстве. Запросите новое
      письмо для сброса пароля.
      <div style={{ marginTop: 12 }}>
        <Link
          to="/forgot-password"
          style={{
            display: 'inline-block',
            padding: '8px 14px',
            borderRadius: 8,
            border: `1px solid ${C.am}55`,
            color: C.am,
            textDecoration: 'none',
            fontFamily: F.head,
            fontWeight: 700,
            fontSize: 12,
            letterSpacing: '.5px',
          }}
        >
          ЗАПРОСИТЬ НОВУЮ ССЫЛКУ
        </Link>
      </div>
    </div>
  );
}

function DoneNotice() {
  return (
    <div
      style={{
        padding: '14px',
        borderRadius: 10,
        background: C.grS,
        border: `1px solid ${C.gr}33`,
        color: C.txt,
        fontSize: 13,
        lineHeight: 1.55,
      }}
    >
      Пароль успешно обновлён. Перенаправляем на страницу входа...
    </div>
  );
}

function ErrorBox({ text }: { text: string }) {
  return (
    <div
      style={{
        padding: '9px 12px',
        borderRadius: 8,
        background: C.reS,
        border: `1px solid ${C.re}44`,
        color: C.re,
        fontSize: 12,
        lineHeight: 1.5,
      }}
    >
      {text}
    </div>
  );
}

const fieldLabelStyle: React.CSSProperties = {
  color: C.sub,
  fontSize: 11,
  fontFamily: F.head,
  fontWeight: 700,
  letterSpacing: '.4px',
  textTransform: 'uppercase',
};

const inputStyle: React.CSSProperties = {
  background: C.surf,
  border: `1px solid ${C.b}`,
  color: C.txt,
  borderRadius: 10,
  padding: '12px 14px',
  outline: 'none',
  fontSize: 14,
  fontFamily: F.body,
};
